import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import {
  cabinetApi,
  formatDate,
  type ItemBody,
  type ItemView,
  type PropertyState,
} from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { usePagedLoader } from '../../api/paging';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { PlusIcon } from '../../shared/icons';
import { versionLabel } from '../../shared/lib/docNumber';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  CardList,
  EmptyState,
  ErrorState,
  LoadingState,
  Muted,
  NoticeBlock,
  PropertyCard,
  Screen,
  Section,
  toast,
} from '../../shared/ui';
import {
  describeProperty,
  PropertyActions,
  PropertyModeSwitch,
  ReviewNote,
  PropertySearch,
} from '../property/propertyKit';
import { ShowMore } from '../review/staffKit';
import { EMPTY_ITEM, ItemForm, type ItemFormValues } from './ItemForm';
import { TagEditor, TagFilter } from './TagFilter';

/**
 * Склад персонажа.
 *
 * У предмета та же пара «опубликованное + на проверке», что и у анкеты:
 * правка одобренного предмета не подменяет то, что читают в реестре, а
 * заводит новую версию и идёт через модерацию. Экран обязан это объяснять,
 * иначе игрок решит, что правка пропала.
 */

const EMPTY_TITLES: Record<PropertyState, string> = {
  published: t('inventory.empty.published.title'),
  requests: t('common.empty.noRequests'),
  archived: t('common.empty.emptyArchive'),
};

const EMPTY_TEXTS: Record<PropertyState, string> = {
  published: t('inventory.empty.published.text'),
  requests: t('inventory.empty.requests.text'),
  archived: t('inventory.empty.archived.text'),
};

/** Что сейчас открыто вместо списка: ничего, создание или правка предмета. */
type Editing = { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; itemId: number };

export function InventoryPanel({ id }: NavIdProps) {
  const back = useBack();
  const [state_, setState_] = useState<PropertyState>('published');
  const [editing, setEditing] = useState<Editing>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<number | null>(null);

  const [tag, setTag] = useState<string | null>(null);
  const search = useSearchTerm();

  // Метки лежат на предмете, а не на версии: проверку они не проходят, и
  // правка предмета их не трогает. Считает их сервер по обеим группам сразу —
  // по загруженному списку не собрать, он постраничный.
  const tagsState = useLoader(() => cabinetApi.itemTags(), []);
  const tags = tagsState.data?.tags ?? [];

  // Выбранную метку могли снять с последнего предмета, пока экран открыт:
  // тогда фильтр молча превращается в «всё», иначе список выглядел бы пустым.
  const activeTag = tag !== null && tags.includes(tag) ? tag : null;

  const state = usePagedLoader<ItemView>(
    (params, signal) =>
      cabinetApi.items(
        { state: state_, tag: activeTag ?? undefined, search: search.term || undefined },
        params,
        signal,
      ),
    [state_, activeTag, search.term],
  );
  const items = state.data?.items ?? [];

  const run = async (action: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await action();
      if (done !== undefined) toast(done);
      setEditing({ kind: 'none' });
      setConfirmArchive(null);
      state.reload();
      tagsState.reload();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const editingItem =
    editing.kind === 'edit' ? (items.find((item) => item.id === editing.itemId) ?? null) : null;

  /*
    Счёт живёт в шапке: строка над списком уезжает вместе с ним.
  */
  return (
    <Screen
      id={id}
      title={t('common.section.inventory')}
      back={back}
      count={state.data?.total}
      unit="item"
    >
      {state.loading && state.data === null ? <LoadingState /> : null}

      {state.error !== null && state.data === null ? (
        <ErrorState
          title={t('inventory.error.load')}
          message={state.error}
          onRetry={state.reload}
        />
      ) : null}

      {editing.kind === 'create' ? (
        <Section>
          <ReviewNote>{t('inventory.note.create')}</ReviewNote>
          <ItemForm
            initial={EMPTY_ITEM}
            submitLabel={t('inventory.action.submit')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(() => cabinetApi.createItem(toBody(values)), t('inventory.toast.created'))
            }
          />
        </Section>
      ) : null}

      {editingItem !== null ? (
        <Section>
          <ReviewNote>{editNote(editingItem)}</ReviewNote>

          <ItemForm
            initial={toValues(editingItem)}
            submitLabel={t('common.action.save')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(
                () => cabinetApi.updateItem(editingItem.id, toBody(values, toValues(editingItem))),
                t('common.saved'),
              )
            }
          />
        </Section>
      ) : null}

      {editing.kind === 'none' && state.data !== null ? (
        <>
          {/* Раздел, поиск с кнопкой и метки — одним блоком над списком и
              именно в этом порядке: что показываем, что ищем, чем сужаем. */}
          <Section>
            <PropertyModeSwitch state={state_} onChange={setState_} disabled={busy} />

            <PropertySearch
              value={search.input}
              onChange={search.setInput}
              placeholder={t('inventory.search.placeholder')}
              action={
                state_ === 'published' ? (
                  <Action
                    tone="action"
                    size="m"
                    before={<PlusIcon />}
                    onClick={() => setEditing({ kind: 'create' })}
                  >
                    {t('common.action.add')}
                  </Action>
                ) : null
              }
            />

            <TagFilter tags={tags} value={activeTag} onChange={setTag} />
          </Section>

          {/* Не пришли смена фильтра или дочитывание: на экране ещё прежние
              строки, и молчать об этом нельзя. */}
          {state.error !== null ? (
            <NoticeBlock
              tone="error"
              title={t('inventory.error.load')}
              action={
                <Action tone="quiet" size="s" onClick={state.reload}>
                  {t('common.action.retry')}
                </Action>
              }
            >
              {state.error}
            </NoticeBlock>
          ) : null}

          <Section>
            {items.length === 0 && state.error === null ? (
              <EmptyState title={EMPTY_TITLES[state_]}>{EMPTY_TEXTS[state_]}</EmptyState>
            ) : null}

            <CardList>
              {items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  busy={busy}
                  confirming={confirmArchive === item.id}
                  onEdit={() => setEditing({ kind: 'edit', itemId: item.id })}
                  onAskArchive={() => setConfirmArchive(item.id)}
                  onCancelArchive={() => setConfirmArchive(null)}
                  onArchive={(reason) =>
                    void run(
                      () => cabinetApi.archiveItem(item.id, reason),
                      t('inventory.toast.archived'),
                    )
                  }
                  onTags={(next) => void run(() => cabinetApi.updateItemTags(item.id, next))}
                />
              ))}
            </CardList>

            <ShowMore page={state.data} onMore={state.loadMore} loading={state.loading} />
          </Section>
        </>
      ) : null}
    </Screen>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Карточка предмета
   ────────────────────────────────────────────────────────────────────────── */

interface ItemCardProps {
  item: ItemView;
  busy: boolean;
  confirming: boolean;
  onEdit: () => void;
  onAskArchive: () => void;
  onCancelArchive: () => void;
  onArchive: (reason: string) => void;
  onTags: (tags: string[]) => void;
}

function ItemCard({
  item,
  busy,
  confirming,
  onEdit,
  onAskArchive,
  onCancelArchive,
  onArchive,
  onTags,
}: ItemCardProps) {
  const status = describeProperty(item);
  const archived = item.deletedAt !== null;

  return (
    <PropertyCard
      image={item.imageId === null ? null : { id: item.imageId }}
      kind="item"
      title={item.title}
      badge={status.badge}
      tone={status.tone}
      // Номер версии и дата — всегда: у вещи с одной версией строка фактов
      // иначе исчезала целиком, и карточка выглядела недогруженной. В макете
      // эта строка есть у каждой карточки.
      meta={[versionLabel(item.versionNo), formatDate(item.submittedAt)]}
      description={item.description}
      hint={status.hint}
      // Метки правятся прямо здесь: проверку они не проходят, версию не заводят.
      // Стоят в шапке под фактами, а не полосой под описанием: это свойство
      // самой вещи, и полосой они читались как отдельный раздел карточки.
      headExtra={
        archived ? undefined : <TagEditor tags={item.tags} disabled={busy} onSave={onTags} />
      }
      actions={
        archived ? (
          <Muted>{t('inventory.card.archived')}</Muted>
        ) : (
          <PropertyActions
            subjectAccusative={t('inventory.card.subject')}
            busy={busy}
            confirming={confirming}
            onEdit={onEdit}
            onAskArchive={onAskArchive}
            onCancelArchive={onCancelArchive}
            onArchive={onArchive}
          />
        )
      }
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Версия ↔ форма
   ────────────────────────────────────────────────────────────────────────── */

function editNote(item: ItemView): string {
  if (item.status === 'rejected') return t('inventory.note.rejected');
  if (item.status !== 'approved') return t('inventory.note.unpublished');
  return t('inventory.note.published');
}

function toValues(item: ItemView): ItemFormValues {
  return {
    title: item.title,
    description: item.description ?? '',
    imageId: item.imageId,
  };
}

function toBody(values: ItemFormValues, initial?: ItemFormValues): ItemBody {
  const body: ItemBody = {
    title: values.title,
    description: values.description,
  };

  if (initial === undefined || values.imageId !== initial.imageId) {
    body.imageId = values.imageId;
  }

  return body;
}
